// ChatPage.jsx - WITH OPTIMISTIC UPDATES AND SCROLLABLE HEADER

import React, { useState, useEffect, useRef } from 'react';
import { Send, RefreshCw } from 'lucide-react';
import Sidebar from './Sidebar';
import api from '../services/api';

const PRITAM_OPENING = `(Pritam is sitting in his room. He looks up briefly as you enter, makes eye contact for a moment, then glances down at his hands with a slight smile) Hello`;

const ChatPage = ({ userData, onLogout, onUpdateSession }) => {
  const [messages, setMessages] = useState(userData.messages || []);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionsList, setSessionsList] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  // Session cache for optimization
  const [sessionsCache, setSessionsCache] = useState({
    data: null,
    timestamp: null,
    expiresIn: 5 * 60 * 1000 // 5 minutes
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sync messages with parent
  useEffect(() => {
    onUpdateSession({ messages });
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // OPTIMIZED: Load sessions with caching
  const loadSessions = async (forceRefresh = false) => {
    const now = Date.now();
    const cacheValid = sessionsCache.data && 
                       sessionsCache.timestamp && 
                       (now - sessionsCache.timestamp) < sessionsCache.expiresIn;
    
    // Use cache if valid and not forcing refresh
    if (cacheValid && !forceRefresh) {
      console.log('✨ Using cached sessions');
      setSessionsList(sessionsCache.data);
      return;
    }
    
    // Fetch fresh data
    try {
      console.log('🔄 Fetching sessions from API');
      const sessions = await api.getUserSessions(userData.userId);
      
      // Update cache
      setSessionsCache({
        data: sessions || [],
        timestamp: now,
        expiresIn: 5 * 60 * 1000
      });
      
      setSessionsList(sessions || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const handleNewChat = async () => {
    // Reset to new chat with Pritam's greeting
    const newMessages = [{
      role: 'ai',
      content: PRITAM_OPENING,
      timestamp: new Date().toISOString()
    }];
    
    setMessages(newMessages);
    onUpdateSession({
      sessionId: null,
      sessionName: null,
      messages: newMessages
    });
    
    // Force refresh because we KNOW sessions will change
    await loadSessions(true);
  };

  const handleLoadSession = async (sessionId) => {
    try {
      const conversation = await api.getConversation(userData.userId, sessionId);
      setMessages(conversation.messages);
      onUpdateSession({
        sessionId: sessionId,
        sessionName: conversation.session_name,
        messages: conversation.messages
      });
    } catch (err) {
      console.error('Error loading session:', err);
      alert('Failed to load session');
    }
  };

  // OPTIMIZED: Retry failed message
  const retryMessage = async (messageId) => {
    // Find the failed message
    const failedMessage = messages.find(m => m.id === messageId);
    if (!failedMessage) return;

    // Remove the failed message and resend
    setMessages(prev => prev.filter(m => m.id !== messageId));
    
    // Use the original content
    const tempInput = failedMessage.content;
    setInputMessage(tempInput);
    
    // Trigger send after a brief delay
    setTimeout(() => {
      document.querySelector('.chat-input-form').requestSubmit();
    }, 100);
  };

  // OPTIMIZED: Send message with optimistic updates
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isTyping) return;

    const userMessage = inputMessage.trim();
    const tempMessageId = `temp-${Date.now()}`; // Temporary ID for tracking
    
    // ✨ OPTIMISTIC UPDATE - Show message immediately
    const optimisticMessage = {
      id: tempMessageId,
      role: 'student',
      content: userMessage,
      timestamp: new Date().toISOString(),
      status: 'sending' // Track status
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setInputMessage(''); // Clear input immediately for better UX
    setIsTyping(true);

    // If no session exists, create one
    let currentSessionId = userData.sessionId;
    
    if (!currentSessionId) {
      try {
        const result = await api.createSession(userData.userId, userData.userName);
        currentSessionId = result.session_id;
        onUpdateSession({
          sessionId: result.session_id,
          sessionName: result.session_name
        });
        
        // Force refresh sessions list since we created a new one
        await loadSessions(true);
      } catch (err) {
        // Mark message as failed
        setMessages(prev =>
          prev.map(msg =>
            msg.id === tempMessageId
              ? { ...msg, status: 'failed', error: err.message }
              : msg
          )
        );
        setIsTyping(false);
        return;
      }
    }

    try {
      // Send to API
      const aiResponse = await api.sendMessage(
        userData.userId,
        currentSessionId,
        userMessage
      );

      // ✅ SUCCESS - Update message status and add AI response
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempMessageId 
            ? { ...msg, status: 'sent' } // Mark as confirmed
            : msg
        ).concat({
          role: 'ai',
          content: aiResponse,
          timestamp: new Date().toISOString()
        })
      );
      
      // DON'T reload sessions - just message count changed, not session list
      // This saves an unnecessary API call!

    } catch (err) {
      console.error('Error sending message:', err);
      
      // ❌ FAILURE - Mark message as failed with error
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempMessageId
            ? { ...msg, status: 'failed', error: err.message }
            : msg
        )
      );
    } finally {
      setIsTyping(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="chat-container">
      {isSidebarVisible && (
        <Sidebar
          userName={userData.userName}
          userEmail={userData.userId}
          sessionsList={sessionsList}
          currentSessionId={userData.sessionId}
          currentSessionName={userData.sessionName}
          onNewChat={handleNewChat}
          onLoadSession={handleLoadSession}
          onLogout={onLogout}
          onClose={() => setIsSidebarVisible(false)}
          onRefreshSessions={() => loadSessions(true)} // Manual refresh option
        />
      )}

      <div className="chat-main">
        {/* Toggle Sidebar Button (when hidden) */}
        {!isSidebarVisible && (
          <button 
            onClick={() => setIsSidebarVisible(true)}
            className="sidebar-toggle-btn"
            aria-label="Open sidebar"
          >
            ☰
          </button>
        )}

        {/* Messages Area - NOW INCLUDES HEADER */}
        <div className="messages-container">
          {/* Chat Header - MOVED INSIDE messages-container so it scrolls */}
          <div className="chat-header-scrollable">
            <h1 className="chat-title">Session with Pritam</h1>
            <p className="chat-subtitle">
              He is a reserved 21 year old student. He does not open up easily, 
              but if you use micro-skills like Open ended questions, paraphrasing, reflection of 
              feeling and take it slow, he certainly would tell you more. You can start with 
              something like 'hello, how are you' or 'what brings you to therapy?' 
              Consider this as your safe space to be bad at and learn by practicing, not via PPTs. When in doubt, empathise ;)
            </p>
          </div>

          {/* Messages */}
          {messages.map((msg, index) => (
            <div
              key={msg.id || index}
              className={`message ${msg.role === 'student' ? 'message-user' : 'message-ai'}`}
            >
              <div className="message-avatar">
                {msg.role === 'student' ? '👨‍⚕️' : '🧑'}
              </div>
              <div className="message-content">
                <div className="message-text">
                  {msg.content}
                                  
                  {msg.status === 'failed' && (
                    <span className="message-status error">
                      <span className="message-status-icon">❌</span> Failed to send
                      <button 
                        onClick={() => retryMessage(msg.id)}
                        className="retry-btn"
                      >
                        <RefreshCw size={14} /> Retry
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="message message-ai">
              <div className="message-avatar">🧑</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - STAYS FIXED AT BOTTOM */}
        <div className="chat-input-container">
          <form onSubmit={handleSendMessage} className="chat-input-form">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message as a therapist..."
              disabled={isTyping}
              className="chat-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isTyping}
              className="btn btn-primary btn-icon"
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
